import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type Dispatch,
  type SetStateAction,
} from "react";

import {
  streamChat,
  batchDeleteResponses,
  listChatSessions,
  getChatSession,
  chatSessionDisplayTitle,
  type ChatChunk,
  type ChatSession,
  type ChatSessionMessage,
  type ChatSessionTenantCount,
} from "../../../client/svcLearnerAi";
import { isTokenExpired } from "../../../utils/authService";

export interface ChatToolProgress {
  toolName?: string;
  // Completion must not erase a start before the display has rendered it.
  pendingStart?: { id: string; toolName: string };
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming?: boolean;
  // Ephemeral activity state; cleared at stream end and never persisted in transcripts.
  toolProgress?: ChatToolProgress;
}

export interface UseChatCoreOptions {
  /** The tenant to send messages to. */
  tenant: string;
  /**
   * Returns a valid tenant-scoped auth token. Called on mount and whenever
   * the tenant changes. Returning null skips the auth header.
   */
  getAuthToken: () => Promise<string | null>;
  /**
   * If true, the sessions list is filtered to `tenant` client-side.
   * Use true for single-tenant contexts (admin, learn app).
   * Use false for sj-admin where all tenants' sessions are shown. Default: false.
   */
  filterSessionsByTenant?: boolean;
  /**
   * Called after a session is loaded, with the session's tenant value.
   * Use in multi-tenant contexts to sync the selected tenant when the user
   * opens a session that belongs to a different tenant (sj-admin only).
   */
  onSessionLoaded?: (sessionTenant: string) => void;
}

export interface UseChatCoreReturn {
  sessionId: string | null;
  sessions: ChatSession[];
  tenantCounts: ChatSessionTenantCount[];
  sessionsLoading: boolean;
  sessionsError: string | null;
  sessionsHasMore: boolean;
  transcriptLoading: boolean;
  messages: ChatMessage[];
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  setError: Dispatch<SetStateAction<string | null>>;
  streaming: boolean;
  error: string | null;
  pendingSelection: [number, number] | null;
  setPendingSelection: Dispatch<SetStateAction<[number, number] | null>>;
  hasMessages: boolean;
  sendMessage: (text: string) => Promise<void>;
  stopStreaming: () => void;
  resetConversation: (idsToDelete?: string[]) => void;
  selectSession: (sessionId: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
}

export function useChatCore({
  tenant,
  getAuthToken,
  filterSessionsByTenant = false,
  onSessionLoaded,
}: UseChatCoreOptions): UseChatCoreReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tenantScopedAuthToken, setTenantScopedAuthToken] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [tenantCounts, setTenantCounts] = useState<ChatSessionTenantCount[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<[number, number] | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const tokenQueueRef = useRef<string[]>([]);
  const rafRef = useRef<number | null>(null);
  const pendingDoneRef = useRef<string | null>(null);
  const pendingDoneSessionRef = useRef<string | null>(null);
  const responseIdsRef = useRef<string[]>([]);

  const stopDrain = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    tokenQueueRef.current = [];
    pendingDoneRef.current = null;
    pendingDoneSessionRef.current = null;
  }, []);

  const startDrain = useCallback(() => {
    if (rafRef.current !== null) return;
    const step = () => {
      const queue = tokenQueueRef.current;
      if (queue.length > 0) {
        const chars = queue.splice(0, 3).join("");
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = { ...last, content: last.content + chars };
          }
          return updated;
        });
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
        const responseId = pendingDoneRef.current;
        if (responseId !== null) {
          const doneSessionId = pendingDoneSessionRef.current;
          pendingDoneRef.current = null;
          pendingDoneSessionRef.current = null;
          if (!doneSessionId) {
            responseIdsRef.current.push(responseId);
          }
          setPreviousResponseId(responseId);
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "assistant") {
              updated[updated.length - 1] = {
                ...last,
                streaming: false,
                toolProgress: undefined,
              };
            }
            return updated;
          });
          setStreaming(false);
        }
      }
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);

  // ── Sessions ─────────────────────────────────────────────────────────────────

  const hasMessages = messages.length > 0;

  const refreshSessions = useCallback(async () => {
    try {
      const result = await listChatSessions(50);
      setSessions(
        filterSessionsByTenant ? result.sessions.filter(s => s.tenant === tenant) : result.sessions
      );
      setTenantCounts(result.tenantCounts);
      setSessionsHasMore(result.hasMore);
      setSessionsError(null);
    } catch {
      setSessionsError("Failed to load chat sessions");
    } finally {
      setSessionsLoading(false);
    }
  }, [tenant, filterSessionsByTenant]);

  // ── Effects ──────────────────────────────────────────────────────────────────

  // Fetch a fresh auth token whenever getAuthToken identity changes (i.e. tenant changed).
  useEffect(() => {
    let cancelled = false;
    setTenantScopedAuthToken(null);
    getAuthToken()
      .then(token => {
        if (!cancelled) setTenantScopedAuthToken(token);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [getAuthToken]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  // Clean up unconsumed OpenAI response objects on unmount.
  useEffect(() => {
    return () => {
      if (responseIdsRef.current.length > 0) {
        batchDeleteResponses(responseIdsRef.current).catch(() => {});
      }
    };
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const resetConversation = useCallback(
    (idsToDelete?: string[]) => {
      abortRef.current?.abort();
      stopDrain();
      const ids = idsToDelete ?? responseIdsRef.current;
      if (ids.length > 0) {
        batchDeleteResponses(ids).catch(() => {});
      }
      responseIdsRef.current = [];
      setMessages([]);
      setPreviousResponseId(null);
      setSessionId(null);
      setError(null);
      setStreaming(false);
    },
    [stopDrain]
  );

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    stopDrain();
    setStreaming(false);
    setMessages(prev => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last?.role === "assistant" && last.streaming) {
        updated[updated.length - 1] = {
          ...last,
          streaming: false,
          toolProgress: undefined,
        };
      }
      return updated;
    });
  }, [stopDrain]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || streaming) return;
      setError(null);

      const authToken =
        tenantScopedAuthToken && !isTokenExpired(tenantScopedAuthToken)
          ? tenantScopedAuthToken
          : await getAuthToken();
      setTenantScopedAuthToken(authToken);

      setMessages(prev => [...prev, { id: crypto.randomUUID(), role: "user", content: text }]);
      setInput("");
      setStreaming(true);
      setMessages(prev => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "",
          streaming: true,
          toolProgress: {},
        },
      ]);

      abortRef.current = new AbortController();

      try {
        await streamChat({
          message: text,
          tenant,
          tenantScopedAuthToken: authToken ?? undefined,
          previousResponseId,
          sessionId,
          onChunk: (chunk: ChatChunk) => {
            if (chunk.type === "token") {
              tokenQueueRef.current.push(...chunk.token);
              startDrain();
            } else if (chunk.type === "toolProgress") {
              const pendingStart = chunk.completed
                ? undefined
                : { id: crypto.randomUUID(), toolName: chunk.toolName };
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant" && last.streaming && last.toolProgress) {
                  updated[updated.length - 1] = {
                    ...last,
                    toolProgress: chunk.completed
                      ? { ...last.toolProgress, toolName: undefined }
                      : { toolName: chunk.toolName, pendingStart },
                  };
                }
                return updated;
              });
            } else if (chunk.type === "done") {
              if (chunk.sessionId) setSessionId(chunk.sessionId);
              pendingDoneRef.current = chunk.responseId;
              pendingDoneSessionRef.current = chunk.sessionId;
              setMessages(prev => clearLastAssistantToolProgress(prev));
              refreshSessions();
              startDrain();
            } else if (chunk.type === "error") {
              stopDrain();
              setError(chunk.error);
              setMessages(prev => prev.slice(0, -1));
              setStreaming(false);
            }
          },
          signal: abortRef.current.signal,
        });
        if (abortRef.current?.signal.aborted) {
          stopDrain();
          setMessages(prev => clearLastAssistantToolProgress(prev));
          setStreaming(false);
        }
      } catch (err: unknown) {
        stopDrain();
        const isAbort =
          (err instanceof Error || err instanceof DOMException) && err.name === "AbortError";
        if (isAbort) {
          setMessages(prev => clearLastAssistantToolProgress(prev));
          setStreaming(false);
          return;
        }
        setError(err instanceof Error ? err.message : "Something went wrong");
        setMessages(prev => prev.slice(0, -1));
        setStreaming(false);
      }
    },
    [
      streaming,
      tenant,
      previousResponseId,
      sessionId,
      tenantScopedAuthToken,
      getAuthToken,
      startDrain,
      stopDrain,
      refreshSessions,
    ]
  );

  const selectSession = useCallback(
    async (nextSessionId: string) => {
      if (streaming || nextSessionId === sessionId) return;
      abortRef.current?.abort();
      stopDrain();
      setTranscriptLoading(true);
      setError(null);
      try {
        const result = await getChatSession(nextSessionId);
        const loadedSession = sessionWithTitleFallback(result.session, result.messages);
        onSessionLoaded?.(loadedSession.tenant);
        setSessionId(loadedSession.sessionId);
        setPreviousResponseId(loadedSession.lastResponseId);
        responseIdsRef.current = [];
        setInput("");
        setPendingSelection(null);
        setSessions(prev => upsertSession(prev, loadedSession));
        setMessages(result.messages.map(chatSessionMessageToMessage));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load chat session");
      } finally {
        setTranscriptLoading(false);
      }
    },
    [streaming, sessionId, stopDrain, onSessionLoaded]
  );

  return {
    sessionId,
    sessions,
    tenantCounts,
    sessionsLoading,
    sessionsError,
    sessionsHasMore,
    transcriptLoading,
    messages,
    input,
    setInput,
    setError,
    streaming,
    error,
    pendingSelection,
    setPendingSelection,
    hasMessages,
    sendMessage,
    stopStreaming,
    resetConversation,
    selectSession,
    refreshSessions,
  };
}

// ── Module-level helpers (pure, no state) ────────────────────────────────────

function clearLastAssistantToolProgress(messages: ChatMessage[]): ChatMessage[] {
  const updated = [...messages];
  const last = updated[updated.length - 1];
  if (last?.role === "assistant" && last.toolProgress) {
    updated[updated.length - 1] = { ...last, toolProgress: undefined };
  }
  return updated;
}

function chatSessionMessageToMessage(message: ChatSessionMessage): ChatMessage {
  return {
    id: message.messageId || crypto.randomUUID(),
    role: message.role,
    content: message.content,
  };
}

function sessionWithTitleFallback(
  session: ChatSession,
  messages: ChatSessionMessage[]
): ChatSession {
  const firstUserMessage = messages.find(m => m.role === "user")?.content?.trim() ?? "";
  const firstMessagePreview = session.firstMessagePreview?.trim() || firstUserMessage;
  return {
    ...session,
    firstMessagePreview,
    title: chatSessionDisplayTitle({ ...session, firstMessagePreview }),
  };
}

function upsertSession(sessions: ChatSession[], session: ChatSession): ChatSession[] {
  const index = sessions.findIndex(item => item.sessionId === session.sessionId);
  if (index === -1) return [session, ...sessions];
  const updated = [...sessions];
  updated[index] = session;
  return updated;
}
